// src/utils/exportJobPDF.js
// Handles Print & Direct PDF Download for Jobs
import API from "../services/api";

const STATUS_LABELS = {
  pending: "Pending", ongoing: "Ongoing", hold: "Hold",
  revisit: "Revisit", completed: "Completed", draft: "Draft",
};

const STATUS_COLORS = {
  pending:   { bg: "#fef3c7", color: "#b45309" },
  ongoing:   { bg: "#dbeafe", color: "#1d4ed8" },
  hold:      { bg: "#ede9fe", color: "#6d28d9" },
  revisit:   { bg: "#ccfbf1", color: "#0f766e" },
  completed: { bg: "#dcfce7", color: "#15803d" },
  draft:     { bg: "#f3e8ff", color: "#7e22ce" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const fmtTime = (t) => {
  if (!t) return "—";
  const parts = t.split(":");
  if (parts.length < 2) return t;
  const hour = parseInt(parts[0], 10);
  return `${hour % 12 || 12}:${parts[1]} ${hour < 12 ? "AM" : "PM"}`;
};

// Convert image URL to base64 with a 1.5s timeout safety; fallback to url on error
const toBase64 = (url) =>
  new Promise((resolve) => {
    if (!url || typeof url !== "string" || url.includes("[object Object]")) {
      return resolve(null);
    }
    const timer = setTimeout(() => resolve(url), 1500);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch {
        resolve(url);
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(url);
    };
    img.src = url;
  });

// Dynamically load html2pdf.js for direct PDF file download
const loadHtml2Pdf = () =>
  new Promise((resolve, reject) => {
    if (window.html2pdf) return resolve(window.html2pdf);
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
    script.onload = () => resolve(window.html2pdf);
    script.onerror = () => reject(new Error("Failed to load PDF generator library"));
    document.head.appendChild(script);
  });

export async function exportJobPDF(jobId, mode = "download") {
  let json;
  try {
    const res = await API.get(`/jobdetails/${jobId}`);
    json = res.data;
  } catch (err1) {
    try {
      const res = await API.get(`/job/${jobId}`);
      json = res.data;
    } catch {
      throw new Error(err1?.response?.data?.message || err1?.message || "Failed to fetch job details");
    }
  }

  if (!json || (!json.success && !json.data)) {
    throw new Error(json?.message || "Failed to fetch job details");
  }

  const data = json.data || json;
  const job = data.job || data;
  const materials = data.issued_materials || data.materials || [];
  const activities = data.articles || data.activities || [];
  const staffList = Array.isArray(data.assigned_staff) ? data.assigned_staff : (Array.isArray(data.staff) ? data.staff : []);
  const staffNames = staffList.map(s => s.name || s).join(", ") || job.staff_name || "—";

  const getArticleStaff = (art) => {
    if (!art) return staffNames || "—";
    if (art.assigned_staff_details) {
      if (Array.isArray(art.assigned_staff_details)) {
        const names = art.assigned_staff_details.map(s => s?.name || s).filter(Boolean).join(", ");
        if (names) return names;
      } else if (typeof art.assigned_staff_details === "object" && art.assigned_staff_details?.name) {
        return art.assigned_staff_details.name;
      }
    }
    if (Array.isArray(art.assigned_staff)) {
      const names = art.assigned_staff.map(s => s?.name || s).filter(Boolean).join(", ");
      if (names) return names;
    } else if (typeof art.assigned_staff === "object" && art.assigned_staff !== null) {
      if (art.assigned_staff.name) return art.assigned_staff.name;
    } else if (art.assigned_staff) {
      const found = staffList.find(s => String(s.id) === String(art.assigned_staff));
      if (found?.name) return found.name;
      return `Staff #${art.assigned_staff}`;
    }
    return staffNames || "—";
  };

  const statusKey = (job.status || "").toLowerCase();
  const statusLabel = STATUS_LABELS[statusKey] || job.status || "Pending";
  const statusColor = STATUS_COLORS[statusKey] || { bg: "#f3f4f6", color: "#374151" };

  // Helper to extract clean photo URL
  const cleanPhotoUrl = (raw) => {
    if (!raw) return null;
    if (typeof raw === "object") {
      raw = raw.url || raw.photo || raw.filename || raw.path || null;
    }
    if (typeof raw !== "string") return null;
    const str = raw.trim();
    if (!str || str === "[object Object]") return null;
    if (str.startsWith("http://") || str.startsWith("https://") || str.startsWith("data:") || str.startsWith("blob:")) {
      return str;
    }
    const clean = str.replace(/^\/+/, "");
    if (clean.includes("/")) {
      return `/${clean}`;
    }
    return `/job_photos/articles/${clean}`;
  };

  // Organize photos
  let beforePhotos = [];
  let duringPhotos = [];
  let afterPhotos = [];

  // 1. Photos from articles (e.g. initial photo)
  activities.forEach(art => {
    const initPhoto = cleanPhotoUrl(art.initial_photo_url || art.initial_photo);
    if (initPhoto) beforePhotos.push(initPhoto);

    if (Array.isArray(art.site_activities)) {
      art.site_activities.forEach(act => {
        const bp = cleanPhotoUrl(act.before_photo_url || act.before_photo);
        if (bp) beforePhotos.push(bp);
        const ap = cleanPhotoUrl(act.after_photo_url || act.after_photo);
        if (ap) afterPhotos.push(ap);
      });
    }
  });

  // 2. Photos from photo_rows
  const rawPhotoRows = Array.isArray(data.photo_rows) ? data.photo_rows : [];
  rawPhotoRows.forEach(row => {
    const bp = cleanPhotoUrl(row.before_photo);
    if (bp) beforePhotos.push(bp);
    (row.activity_before_photos || []).forEach(item => {
      const u = cleanPhotoUrl(item);
      if (u) beforePhotos.push(u);
    });
    (row.gallery_before_photos || []).forEach(item => {
      const u = cleanPhotoUrl(item);
      if (u) beforePhotos.push(u);
    });

    const jc = cleanPhotoUrl(row.job_card_photo);
    if (jc) duringPhotos.push(jc);

    const ap = cleanPhotoUrl(row.after_photo);
    if (ap) afterPhotos.push(ap);
    (row.activity_after_photos || []).forEach(item => {
      const u = cleanPhotoUrl(item);
      if (u) afterPhotos.push(u);
    });
    (row.gallery_after_photos || []).forEach(item => {
      const u = cleanPhotoUrl(item);
      if (u) afterPhotos.push(u);
    });
    (row.after_photos || []).forEach(item => {
      const u = cleanPhotoUrl(item);
      if (u) afterPhotos.push(u);
    });
  });

  // 3. Fallback for generic data.photos
  if (Array.isArray(data.photos)) {
    data.photos.forEach(p => {
      const u = cleanPhotoUrl(p);
      if (!u) return;
      const type = (p?.type || "").toLowerCase();
      if (type.includes("before")) beforePhotos.push(u);
      else if (type.includes("after")) afterPhotos.push(u);
      else duringPhotos.push(u);
    });
  }

  // Deduplicate URLs
  beforePhotos = [...new Set(beforePhotos)];
  duringPhotos = [...new Set(duringPhotos)];
  afterPhotos  = [...new Set(afterPhotos)];

  const photoSections = [
    { label: "Before", urls: beforePhotos },
    { label: "Start",  urls: duringPhotos },
    { label: "Finish", urls: afterPhotos },
  ];

  for (const section of photoSections) {
    section.base64 = await Promise.all(section.urls.map(toBase64));
  }

  const infoRows = [
    { label: "Job Card No",  value: job.job_no || `#${job.id}` },
    { label: "Client Name",  value: job.client_name },
    { label: "Location",     value: job.location },
    { label: "Address",      value: job.address },
    { label: "Work Type",    value: job.work_type || job.job_title },
    { label: "Assigned Staff", value: staffNames },
    { label: "Created Date", value: fmtDate(job.created_at) },
  ].filter(r => r.value);

  const infoHTML = infoRows.map(r => `
    <tr>
      <td class="info-label">${r.label}</td>
      <td class="info-value">${r.value}</td>
    </tr>
  `).join("");

  const articlesHTML = activities.length > 0 ? `
    <div class="section">
      <h2>Job Card — Article Wise Details</h2>
      ${activities.map((art, idx) => {
        const artName = art.article || art.name || art.code || `Article ${idx + 1}`;
        const artMats = Array.isArray(art.materials) ? art.materials : [];
        return `
          <div style="border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; margin-bottom: 12px; background: #fafafa;">
            <div style="font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 4px;">
              ${idx + 1}. ${artName}
            </div>
            <div style="font-size: 12px; color: #64748b; margin-bottom: 8px;">
              Location: ${art.location || "—"} &nbsp;|&nbsp; Est. Time: ${art.est_time || "—"} &nbsp;|&nbsp; Staff: ${getArticleStaff(art)}
            </div>
            ${art.description ? `<div style="font-size: 12px; color: #334155; margin-bottom: 8px;"><strong>Description:</strong> ${art.description}</div>` : ""}

            <div style="margin-top: 6px;">
              <strong style="font-size: 11px; text-transform: uppercase; color: #475569;">Estimated Materials:</strong>
              <table class="data-table" style="margin-top: 4px;">
                <thead><tr><th>Material Name</th><th>Est. Qty</th><th>Unit</th></tr></thead>
                <tbody>
                  ${artMats.length === 0 ? '<tr><td colSpan="3" style="color: #94a3b8; font-style: italic;">No estimated materials specified</td></tr>' :
                    artMats.map(m => `
                      <tr>
                        <td>${m.material_name || m.name || m.material || m.code || "—"}</td>
                        <td>${m.quantity || m.qty || 1}</td>
                        <td>${m.unit || "pcs"}</td>
                      </tr>
                    `).join("")}
                </tbody>
              </table>
            </div>
          </div>
        `;
      }).join("")}
    </div>
  ` : "";

  const materialsHTML = materials.length > 0 ? `
    <div class="section">
      <h2>Materials Issued (Total)</h2>
      <table class="data-table">
        <thead><tr><th>Material Name</th><th>Issued Qty</th><th>Used Qty</th><th>Return Qty</th><th>Unit</th></tr></thead>
        <tbody>
          ${materials.map(m => `
            <tr>
              <td>${m.material_name || m.name}</td>
              <td><span class="qty-badge">${m.issued_qty || m.quantity || 1}</span></td>
              <td>${m.used_qty ?? m.used ?? 0}</td>
              <td>${m.return_qty ?? 0}</td>
              <td>${m.unit || "pcs"}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  ` : "";

  const activityLogHTML = activities.length > 0 ? `
    <div class="section">
      <h2>Activity Status & Timings</h2>
      <table class="data-table">
        <thead>
          <tr>
            <th>Activity</th>
            <th>Field Staff</th>
            <th>Date & Time</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${activities.map(a => `
            <tr>
              <td>${a.activity_type || a.name || 'Site Activity'}</td>
              <td>${getArticleStaff(a)}</td>
              <td>${fmtDate(a.created_at || a.start_time)} ${fmtTime(a.start_time || a.created_at)}</td>
              <td><span class="qty-badge" style="background:#e0f2fe; color:#0369a1;">${a.status || 'Active'}</span></td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  ` : "";

  const photosHTML = photoSections.map(section => {
    const validBase64 = (section.base64 || []).filter(Boolean);
    if (!validBase64.length) return `
      <div class="photo-group">
        <h3 class="photo-group-title">${section.label} Photos</h3>
        <p class="no-photos">No ${section.label.toLowerCase()} photos uploaded.</p>
      </div>
    `;
    return `
      <div class="photo-group">
        <h3 class="photo-group-title">${section.label} Photos</h3>
        <div class="photo-grid">
          ${validBase64.map((src, i) => `
            <div class="photo-item">
              <img src="${src}" alt="${section.label} ${i + 1}" />
              <div class="photo-caption">${section.label} — ${i + 1}</div>
            </div>
          `).join("")}
        </div>
      </div>
    `;
  }).join("");

  const generatedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Job #${job.id} — Report</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 13px;
      color: #0f172a;
      background: #fff;
      padding: 0;
    }
    .page-wrapper {
      width: 794px;
      margin: 0 auto;
      padding: 32px 40px;
      background: #ffffff;
    }
    .report-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #2563eb;
      padding-bottom: 18px;
      margin-bottom: 28px;
    }
    .report-title {
      font-size: 24px;
      font-weight: 800;
      color: #0f172a;
      margin-bottom: 4px;
    }
    .report-meta {
      font-size: 12px;
      color: #64748b;
    }
    .report-logo {
      text-align: right;
    }
    .report-logo-name {
      font-size: 18px;
      font-weight: 800;
      color: #2563eb;
    }
    .report-logo-sub {
      font-size: 11px;
      color: #64748b;
      margin-top: 2px;
    }
    .status-badge {
      display: inline-block;
      padding: 4px 14px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 700;
      background: ${statusColor.bg};
      color: ${statusColor.color};
      margin-top: 8px;
    }
    .section {
      margin-bottom: 28px;
    }
    h2 {
      font-size: 13px;
      font-weight: 800;
      color: #0f172a;
      margin-bottom: 12px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e2e8f0;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .info-table {
      width: 100%;
      border-collapse: collapse;
    }
    .info-label {
      width: 140px;
      padding: 8px 10px 8px 0;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.04em;
      color: #64748b;
      text-transform: uppercase;
      vertical-align: top;
      border-bottom: 1px solid #f1f5f9;
    }
    .info-value {
      padding: 8px 0;
      font-size: 13.5px;
      color: #0f172a;
      border-bottom: 1px solid #f1f5f9;
    }
    .data-table {
      width: 100%;
      border-collapse: collapse;
    }
    .data-table thead {
      background: #f8fafc;
    }
    .data-table th {
      padding: 9px 12px;
      text-align: left;
      font-size: 10.5px;
      font-weight: 800;
      letter-spacing: 0.055em;
      color: #64748b;
      text-transform: uppercase;
      border-bottom: 1px solid #e2e8f0;
    }
    .data-table td {
      padding: 9px 12px;
      font-size: 12.5px;
      color: #334155;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: middle;
    }
    .qty-badge {
      display: inline-block;
      background: #f1f5f9;
      color: #0f172a;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 12px;
      font-weight: 600;
    }
    .photo-group {
      margin-bottom: 20px;
    }
    h3.photo-group-title {
      font-size: 12px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 8px;
    }
    .photo-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    .photo-item {
      width: 180px;
    }
    .photo-item img {
      width: 180px;
      height: 135px;
      object-fit: cover;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      display: block;
    }
    .photo-caption {
      font-size: 10px;
      color: #94a3b8;
      text-align: center;
      margin-top: 4px;
    }
    .no-photos {
      font-size: 12px;
      color: #94a3b8;
      font-style: italic;
    }
    .report-footer {
      border-top: 1px solid #e2e8f0;
      margin-top: 36px;
      padding-top: 14px;
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #94a3b8;
    }
    @media print {
      body { padding: 0; }
      .page-wrapper { padding: 24px 32px; max-width: 100%; }
      h2 { break-after: avoid; }
      .section { break-inside: avoid; }
      .photo-item { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="page-wrapper">
    <div class="report-header">
      <div>
        <div class="report-title">Job Report — ${job.job_no || `#${job.id}`}</div>
        <div class="report-meta">Generated on ${generatedOn}</div>
        <div><span class="status-badge">${statusLabel}</span></div>
      </div>
      <div class="report-logo">
        <div class="report-logo-name">Four Leaf IT</div>
        <div class="report-logo-sub">Job Tracking System</div>
      </div>
    </div>

    <div class="section">
      <h2>Basic Information</h2>
      <table class="info-table">
        <tbody>${infoHTML}</tbody>
      </table>
    </div>

    ${articlesHTML}
    ${materialsHTML}
    ${activityLogHTML}

    <div class="section">
      <h2>Photos</h2>
      ${photosHTML}
    </div>

    <div class="report-footer">
      <span>Four Leaf Job Tracking — Confidential</span>
      <span>${generatedOn}</span>
    </div>
  </div>

  <script>
    window.onload = function() {
      window.print();
    };
  </script>
</body>
</html>
  `;

  if (mode === "download") {
    try {
      const html2pdf = await loadHtml2Pdf();

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.left = "0";
      iframe.style.top = "0";
      iframe.style.width = "800px";
      iframe.style.height = "1000px";
      iframe.style.zIndex = "-99999";
      iframe.style.opacity = "0.01";
      iframe.style.pointerEvents = "none";
      iframe.style.border = "none";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html.replace(/<script>[\s\S]*?<\/script>/gi, ""));
      doc.close();

      await new Promise((r) => setTimeout(r, 300));

      const element = doc.querySelector(".page-wrapper") || doc.body;

      const cleanJobNo = (job.job_no || `#${job.id}`).toString().replace(/[^\w-]/g, '_');
      const filename = `Job_Report_${cleanJobNo}.pdf`;

      const opt = {
        margin:       0,
        filename:     filename,
        image:        { type: "jpeg", quality: 0.98 },
        html2canvas:  {
          scale: 2,
          useCORS: true,
          logging: false,
          width: 794,
          windowWidth: 794,
          scrollX: 0,
          scrollY: 0
        },
        jsPDF:        { unit: "px", format: [794, 1123], orientation: "portrait" }
      };

      await html2pdf().set(opt).from(element).save();
      document.body.removeChild(iframe);
      return;
    } catch (err) {
      console.error("Direct PDF download error:", err);
      alert("Failed to download PDF file. Please try again.");
      return;
    }
  }

  // Print Mode — using offscreen iframe with fallback
  try {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.top = "0";
    iframe.style.left = "-9999px";
    iframe.style.width = "1024px";
    iframe.style.height = "768px";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    // Remove auto-print script so we control trigger timing
    doc.write(html.replace(/<script>[\s\S]*?<\/script>/gi, ""));
    doc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (printErr) {
        console.warn("Iframe print error, falling back to window:", printErr);
        const win = window.open("", "_blank");
        if (win) {
          win.document.open();
          win.document.write(html);
          win.document.close();
        }
      }

      const cleanup = () => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      };

      if (iframe.contentWindow) {
        iframe.contentWindow.onafterprint = cleanup;
      }
      setTimeout(cleanup, 60000);
    }, 400);
  } catch (err) {
    console.error("Print error:", err);
    const win = window.open("", "_blank");
    if (win) {
      win.document.open();
      win.document.write(html);
      win.document.close();
    } else {
      alert("Failed to open print dialog. Please allow popups if blocked.");
    }
  }
}